import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../../helpers/AuthContext';
import { motion } from 'framer-motion';
import { HiOutlineLogout, HiChevronDown, HiChevronUp, HiMenu } from 'react-icons/hi';
import { DASHBOARD_SIDEBAR_LINKS, DASHBOARD_SIDEBAR_BOTTOM_LINKS } from '../../lib/constants';
import tw from 'twin.macro';
import logo from '../../images/logo.png';

const Container = styled(motion.div)`
  ${tw`bg-purple-900 p-2 flex flex-col flex-shrink-0`}
  width: 13rem;
  background-color: #5b1ced;
  transition: background-color 0.3s ease;
  overflow-y: auto;
  max-height: 100vh;
  position: fixed;
  left: ${({ isOpen }) => (isOpen ? '0' : '-13rem')}; /* Adjust based on sidebar width */
  top: 0;
  bottom: 0;
  z-index: 40;

  @media (min-width: 769px) {
    position: relative;
    left: 0;
  }
`;

const Logo = styled.div`
  ${tw`flex items-center justify-center gap-2 px-2 py-2 text-xl`}
  background-color: white;
  color: #5b1ced;
  border-radius: 4px;
`;

const LinkContainer = styled.div`
  ${tw`py-4 flex flex-1 flex-col gap-0 text-sm`}
`;

const BottomLinkContainer = styled.div`
  ${tw`flex flex-col gap-0 pt-2 text-sm`}
`;

const StyledLink = styled(Link)`
  ${tw`flex items-center gap-2 font-light px-2 py-2 rounded-sm text-sm text-white cursor-pointer`}
  transition: background-color 0.3s ease;
  width: 100%;
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  ${({ active }) => active && tw`bg-purple-700`}
  &:not(:last-child) {
    ${tw`border-b border-gray-600 `}
  }
`;

const StyledLinkWithDropdown = styled(StyledLink)`
  justify-content: space-between;
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const SubLinkContainer = styled.div`
  ${tw`pl-6 flex flex-col gap-0`}
`;

const SubLink = styled(Link)`
  ${tw`flex items-center gap-2 font-light px-2 py-2 rounded-sm text-sm text-white cursor-pointer`}
  transition: background-color 0.3s ease;
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
  ${({ active }) => active && tw`bg-purple-700`}
  &:not(:last-child) {
    ${tw`border-b border-gray-600`}
  }
`;

const LogoutLink = styled.div`
  ${tw`flex items-center gap-2 font-light px-2 py-2 rounded-sm text-base cursor-pointer text-white`}
  transition: background-color 0.3s ease;
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;



const ToggleButton = styled.button`
  ${tw`fixed p-2 bg-purple-900 text-white rounded-full z-50`}
  top: 1rem;
  left: 1rem;
  @media (min-width: 769px) {
    display: none;
  }
`;

const Overlay = styled.div`
  ${tw`fixed inset-0 z-30`}
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};

  @media (min-width: 769px) {
    display: none;
  }
`;

export default function Sidebar() {
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isAccountsOpen, setIsAccountsOpen] = useState(false);
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);

  const handleAccountsClick = () => {
    setIsAccountsOpen(!isAccountsOpen);
  };

  const handleLogout = async () => {
    await logout(); // Perform logout logic
    navigate('/'); // Redirect to home page
  };

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  return (
    <>
      <ToggleButton onClick={toggleSidebar}>
        <HiMenu size={24} />
      </ToggleButton>
      <Overlay isOpen={isOpen} onClick={toggleSidebar} />
      <Container isOpen={isOpen}>
        <Link to={"/"}>
          <Logo>
            <img src={logo} alt="FalconAlgo Logo" style={{ width: '40px', height: '40px' }} />
            <span>FalconAlgo</span>
          </Logo>
        </Link>
        <LinkContainer>
          {DASHBOARD_SIDEBAR_LINKS && DASHBOARD_SIDEBAR_LINKS.map((link) => (
            <React.Fragment key={link.key}>
              {link.label === 'Accounts' ? (
                <>
                  <StyledLinkWithDropdown
                    onClick={handleAccountsClick}
                    active={isAccountsOpen}
                    style={{ backgroundColor: isAccountsOpen ? '#4c10af' : 'transparent' }}
                  >
                    <div>{link.icon}</div>
                    <div style={{ flexGrow: 1 }}>{link.label}</div>
                    <div>{isAccountsOpen ? <HiChevronUp /> : <HiChevronDown />}</div>
                  </StyledLinkWithDropdown>
                  {isAccountsOpen && (
                    <SubLinkContainer>
                      <SubLink to="/layout/accounts/funds" active={pathname === '/layout/accounts/funds'}>
                        Funds
                      </SubLink>
                      <SubLink to="/layout/accounts/margin" active={pathname === '/layout/accounts/margin'}>
                        Margin
                      </SubLink>
                      <SubLink to="/layout/accounts/demat-holdings" active={pathname === '/layout/accounts/demat-holdings'}>
                        Demat Holdings
                      </SubLink>
                      <SubLink to="/layout/accounts/portfolio-holdings" active={pathname === '/layout/accounts/portfolio-holdings'}>
                        Portfolio Holdings
                      </SubLink>
                      <SubLink to="/layout/accounts/portfolio-positions" active={pathname === '/layout/accounts/portfolio-positions'}>
                        Portfolio Positions
                      </SubLink>
                    </SubLinkContainer>
                  )}
                </>
              ) : (
                <SidebarLink key={link.key} link={link} pathname={pathname} />
              )}
            </React.Fragment>
          ))}
        </LinkContainer>
        <BottomLinkContainer>
          {DASHBOARD_SIDEBAR_BOTTOM_LINKS && DASHBOARD_SIDEBAR_BOTTOM_LINKS.map((link) => (
            <SidebarLink key={link.key} link={link} pathname={pathname} />
          ))}
          <LogoutLink onClick={handleLogout}>
            <HiOutlineLogout />
            <span>Logout</span>
          </LogoutLink>
        </BottomLinkContainer>
      </Container>
    </>
  );
}

function SidebarLink({ link, pathname }) {
  return (
    <StyledLink to={link.path} active={pathname === link.path}>
      <span>{link.icon}</span>
      <span>{link.label}</span>
    </StyledLink>
  );
}
